import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: ${({ theme, $isScrolled }) => 
    $isScrolled ? `${theme.colors.primary.main}f2` : `${theme.colors.primary.main}e6`};
  backdrop-filter: blur(10px);
  border-bottom: ${({ theme, $isScrolled }) => 
    $isScrolled ? `1px solid ${theme.colors.secondary.light}33` : 'none'};
  z-index: 1000;
  transition: ${({ theme }) => theme.transitions.smooth};
  padding: ${({ theme, $isScrolled }) => 
    $isScrolled ? `${theme.spacing.sm} 0` : `${theme.spacing.md} 0`};
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 0 ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0 ${({ theme }) => theme.spacing.sm};
  }
`;

const Brand = styled.div`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme, $isScrolled }) => 
    $isScrolled ? theme.fontSizes.xl : theme.fontSizes['2xl']};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.white};
  transition: ${({ theme }) => theme.transitions.smooth};
  letter-spacing: 0.5px;
`;

const Navigation = styled.nav.attrs({ role: 'navigation', 'aria-label': 'Main navigation' })`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: ${({ $isOpen }) => $isOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: ${({ theme }) => theme.colors.primary.main};
    flex-direction: column;
    padding: ${({ theme }) => theme.spacing.md};
    border-top: 1px solid ${({ theme }) => theme.colors.secondary.light}33;
  }
`;

const NavLink = styled.a`
  font-family: ${({ theme }) => theme.fonts.secondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.secondary.light};
  text-decoration: none;
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border-radius: 4px;
  transition: ${({ theme }) => theme.transitions.fast};
  position: relative;
  cursor: pointer;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colors.white};
    background: ${({ theme }) => `${theme.colors.secondary.light}1a`};
    outline: none;
  }

  &:active,
  &.active {
    color: ${({ theme }) => theme.colors.white};
    background: ${({ theme }) => `${theme.colors.secondary.light}33`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.sm};
    font-size: ${({ theme }) => theme.fontSizes.base};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: 4px;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover,
  &:focus {
    background: ${({ theme }) => `${theme.colors.secondary.light}1a`};
    outline: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const Header = () => {
  console.log('Header updated - contrast and colors fixed');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      const headerHeight = 80; // Adjust for fixed header
      const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false); // Close mobile menu after click
  };

  const toggleMobileMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <HeaderContainer $isScrolled={isScrolled}>
      <HeaderContent>
        <Brand $isScrolled={isScrolled}>
          Antony Haines
        </Brand>
        
        <Navigation $isOpen={isMenuOpen}>
          <NavLink 
            href="#hero" 
            onClick={(e) => handleNavClick(e, 'hero')}
          >
            Home
          </NavLink>
          <NavLink 
            href="#about" 
            onClick={(e) => handleNavClick(e, 'about')}
          >
            About
          </NavLink>
          <NavLink 
            href="#services" 
            onClick={(e) => handleNavClick(e, 'services')}
          >
            Services
          </NavLink>
          <NavLink 
            href="#case-study" 
            onClick={(e) => handleNavClick(e, 'case-study')}
          >
            Results
          </NavLink>
          <NavLink 
            href="#contact" 
            onClick={(e) => handleNavClick(e, 'contact')}
          >
            Contact
          </NavLink>
        </Navigation>

        <MobileMenuButton 
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? 'Close' : 'Menu'}
        </MobileMenuButton>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;